/**
 * Building, filtering, and locally traversing the graph. All pure.
 *
 * Nodes and links come out SORTED BY entranceAt so the creation-order
 * animation is a monotonically growing prefix rather than a per-frame
 * membership test.
 */
import { folderColor } from "./palette";
import { compileQuery, matchNode, type CompiledQuery, type QueryTarget } from "./query";
import {
  EMPTY_GRAPH,
  nodeKey,
  type GraphAdjacency,
  type GraphCorpus,
  type GraphData,
  type GraphInput,
  type GraphLink,
  type GraphLinkKind,
  type GraphNode,
  type GraphStats,
} from "./types";
import type { GraphFilters, GraphGroup, GraphLocalOptions } from "@/store/graph";

function emptyStats(): GraphStats {
  return { notes: 0, tags: 0, attachments: 0, unresolved: 0, links: 0, orphans: 0, titleCollisions: 0 };
}

function indexGraph(nodes: GraphNode[], links: GraphLink[], titleCollisions: number): GraphData {
  const byId = new Map<string, GraphNode>();
  for (const n of nodes) byId.set(n.id, n);

  const adjacency = new Map<string, GraphAdjacency>();
  const adj = (id: string): GraphAdjacency => {
    let a = adjacency.get(id);
    if (!a) {
      a = { out: new Set(), in: new Set() };
      adjacency.set(id, a);
    }
    return a;
  };
  for (const n of nodes) adj(n.id);
  for (const l of links) {
    adj(l.source).out.add(l.target);
    adj(l.target).in.add(l.source);
  }

  const stats = emptyStats();
  stats.titleCollisions = titleCollisions;
  stats.links = links.length;
  for (const n of nodes) {
    const a = adjacency.get(n.id) as GraphAdjacency;
    n.degree = a.out.size + a.in.size;
    if (n.degree === 0) stats.orphans++;
    if (n.kind === "note") stats.notes++;
    else if (n.kind === "tag") stats.tags++;
    else if (n.kind === "attachment") stats.attachments++;
    else stats.unresolved++;
  }

  // Creation-order first, so playback is a growing prefix rather than a
  // per-frame membership test.
  nodes.sort((a, b) => a.entranceAt - b.entranceAt || a.id.localeCompare(b.id));
  for (let i = 0; i < nodes.length; i++) (nodes[i] as GraphNode).index = i;
  for (const l of links) {
    l.maxIndex = Math.max(byId.get(l.source)?.index ?? 0, byId.get(l.target)?.index ?? 0);
  }
  // A link is live exactly when both endpoints are, so ordering by the later
  // endpoint makes the live links a prefix too.
  links.sort((a, b) => a.maxIndex - b.maxIndex);
  return { nodes, links, byId, adjacency, stats };
}

function dedupeLower(values: Iterable<string>): string[] {
  const out = new Set<string>();
  for (const v of values) {
    const t = v.trim().toLowerCase();
    if (t) out.add(t);
  }
  return [...out];
}

/** The full, unfiltered graph. Cards, folders and task lists are never nodes. */
export function buildGraph(input: GraphInput): GraphData {
  const nodes = new Map<string, GraphNode>();
  const links: GraphLink[] = [];
  const seenLinks = new Set<string>();

  const addLink = (source: string, target: string, kind: GraphLinkKind) => {
    // self-links are dropped, matching Obsidian
    if (source === target) return;
    const key = `${source}|${target}|${kind}`;
    if (seenLinks.has(key)) return;
    seenLinks.add(key);
    const a = nodes.get(source);
    const b = nodes.get(target);
    links.push({ source, target, kind, entranceAt: Math.max(a?.entranceAt ?? 0, b?.entranceAt ?? 0), maxIndex: 0 });
  };

  // pass 1: note nodes, so every edge in pass 2 can resolve
  const usable: { row: GraphInput["rows"][number]; id: string }[] = [];
  for (const row of input.rows) {
    const item = input.items.get(row.id);
    if (!item) continue; // stale mirror entry
    if (item.type !== "note") continue; // CARDS EXCLUDED (invariant 11)
    if (item.trashedAt || item.archivedAt) continue;
    const id = nodeKey.note(item.id);
    const path = input.paths.get(item.id) ?? "";
    // the path's last segment is the note itself, so a "/" means "in a folder"
    const slash = path.indexOf("/");
    const topFolder = slash > 0 ? path.slice(0, slash).toLowerCase() : "";
    nodes.set(id, {
      id,
      index: 0,
      kind: "note",
      label: item.title.trim() || "Untitled",
      itemId: item.id,
      path,
      pathLower: path.toLowerCase(),
      titleLower: item.title.trim().toLowerCase(),
      ...(topFolder ? { autoColor: folderColor(topFolder) } : {}),
      tags: dedupeLower([...(item.tags ?? []), ...row.hashtags]),
      createdAt: item.createdAt,
      updatedAt: item.updatedAt,
      degree: 0,
      entranceAt: item.createdAt || 0,
    });
    usable.push({ row, id });
  }

  // A derived node (tag, attachment, phantom) exists as soon as the earliest
  // note referencing it exists, so playback never draws a dangling edge.
  const touch = (id: string, at: number) => {
    const n = nodes.get(id);
    if (n && at < n.entranceAt) n.entranceAt = at;
  };

  // pass 2: edges and the derived nodes
  for (const { row, id: src } of usable) {
    const srcNode = nodes.get(src) as GraphNode;
    const at = srcNode.entranceAt;

    for (const target of row.links) {
      const hit = input.noteByTitle.get(target);
      const hitId = hit ? nodeKey.note(hit) : undefined;
      if (hitId && nodes.has(hitId)) {
        addLink(src, hitId, "ref");
        continue;
      }
      const pid = nodeKey.unresolved(target);
      if (!nodes.has(pid)) {
        nodes.set(pid, {
          id: pid,
          index: 0,
          kind: "unresolved",
          label: target,
          pathLower: "",
          titleLower: target,
          tags: [],
          resolvesOffGraph: input.offGraphTitles.has(target),
          degree: 0,
          entranceAt: at,
        });
      }
      touch(pid, at);
      addLink(src, pid, "unresolved");
    }

    for (const tag of srcNode.tags) {
      const tid = nodeKey.tag(tag);
      if (!nodes.has(tid)) {
        nodes.set(tid, {
          id: tid,
          index: 0,
          kind: "tag",
          label: `#${tag}`,
          pathLower: "",
          titleLower: tag,
          tags: [tag],
          degree: 0,
          entranceAt: at,
        });
      }
      touch(tid, at);
      addLink(src, tid, "tag");
    }

    for (const key of row.attachments) {
      const aid = nodeKey.attachment(key);
      if (!nodes.has(aid)) {
        const name = row.attachmentNames[key] ?? key;
        const href = row.attachmentHrefs[key];
        nodes.set(aid, {
          id: aid,
          index: 0,
          kind: "attachment",
          label: name,
          pathLower: "",
          titleLower: name.toLowerCase(),
          tags: [],
          ...(href ? { href } : {}),
          degree: 0,
          entranceAt: at,
        });
      }
      touch(aid, at);
      addLink(src, aid, "attachment");
    }
  }

  // entranceAt on derived nodes may have moved down, so recompute link times
  for (const l of links) {
    l.entranceAt = Math.max(nodes.get(l.source)?.entranceAt ?? 0, nodes.get(l.target)?.entranceAt ?? 0);
  }

  return indexGraph([...nodes.values()], links, input.collisions);
}

function targetOf(n: GraphNode, corpus: GraphCorpus | undefined, needsContent: boolean): QueryTarget {
  const text = needsContent && n.kind === "note" && n.itemId ? corpus?.get(n.itemId) : undefined;
  return {
    titleLower: n.titleLower,
    pathLower: n.pathLower,
    tags: n.tags,
    kind: n.kind,
    ...(text !== undefined ? { text } : {}),
  };
}

/**
 * Filter order is load-bearing.
 *
 * Degree is recomputed AFTER the kind and query gates and BEFORE the orphan
 * gate, so turning Tags off correctly makes a tag-only note an orphan. That is
 * what Obsidian does, and doing it in any other order gets it wrong.
 */
export function applyFilters(
  full: GraphData,
  filters: GraphFilters,
  groups: GraphGroup[],
  corpus?: GraphCorpus,
): GraphData {
  const q = compileQuery(filters.search);

  // 1. kind gates
  const kept = new Set<string>();
  for (const n of full.nodes) {
    if (n.kind === "tag" && !filters.showTags) continue;
    if (n.kind === "attachment" && !filters.showAttachments) continue;
    if (n.kind === "unresolved" && filters.existingOnly) continue;
    kept.add(n.id);
  }

  // 2. query gate, note nodes only
  if (!q.isTrue) {
    for (const n of full.nodes) {
      if (n.kind !== "note" || !kept.has(n.id)) continue;
      if (!matchNode(q, targetOf(n, corpus, q.needsContent))) kept.delete(n.id);
    }
    // a tag/attachment/phantom is not a file: it survives only while some
    // surviving note still references it
    for (const n of full.nodes) {
      if (n.kind === "note" || !kept.has(n.id)) continue;
      const a = full.adjacency.get(n.id);
      const alive =
        !!a &&
        ([...a.in].some((id) => kept.has(id)) || [...a.out].some((id) => kept.has(id)));
      if (!alive) kept.delete(n.id);
    }
  }

  let nodes = full.nodes.filter((n) => kept.has(n.id)).map((n) => ({ ...n, degree: 0 }));
  let links = full.links.filter((l) => kept.has(l.source) && kept.has(l.target));

  // 3. degree recompute (inside indexGraph), 4. orphan gate
  let out = indexGraph(nodes, links, full.stats.titleCollisions);
  if (!filters.showOrphans) {
    const live = new Set(out.nodes.filter((n) => n.degree > 0).map((n) => n.id));
    nodes = out.nodes.filter((n) => live.has(n.id)).map((n) => ({ ...n, degree: 0 }));
    links = out.links.filter((l) => live.has(l.source) && live.has(l.target));
    out = indexGraph(nodes, links, full.stats.titleCollisions);
  }

  // 5. colors only; groups never change membership
  assignGroups(out.nodes, groups, corpus, q.needsContent);
  return out;
}

/**
 * FIRST matching group wins.
 *
 * The group list is a visible, drag-reorderable stack, and "the one on top
 * wins" is the only rule that shape communicates without extra chrome. It also
 * makes the reorder handle mean something: drag a group up to promote it.
 */
export function assignGroups(
  nodes: GraphNode[],
  groups: GraphGroup[],
  corpus: GraphCorpus | undefined,
  needsContent: boolean,
): void {
  if (!groups.length) {
    for (const n of nodes) delete n.groupColor;
    return;
  }
  const compiled: { q: CompiledQuery; color: string }[] = groups
    .filter((g) => g.query.trim())
    .map((g) => ({ q: compileQuery(g.query), color: g.color }));
  const wantsText = needsContent || compiled.some((c) => c.q.needsContent);
  for (const n of nodes) {
    delete n.groupColor;
    const t = targetOf(n, corpus, wantsText);
    for (const c of compiled) {
      if (matchNode(c.q, t)) {
        n.groupColor = c.color;
        break;
      }
    }
  }
}

/**
 * The local graph: BFS out from one note.
 *
 * Filters run FIRST, over the whole graph, so a hidden kind can never act as
 * a traversal hop.
 */
export function localGraph(
  full: GraphData,
  rootItemId: string | null,
  filters: GraphFilters,
  groups: GraphGroup[],
  local: GraphLocalOptions,
  corpus?: GraphCorpus,
): GraphData {
  if (!rootItemId) return EMPTY_GRAPH;
  const g = applyFilters(full, filters, groups, corpus);
  const root = nodeKey.note(rootItemId);
  if (!g.byId.has(root)) return EMPTY_GRAPH;

  const visited = new Map<string, number>([[root, 0]]);
  const treeEdges: GraphLink[] = [];
  let frontier = [root];
  const maxDepth = Math.max(1, Math.min(5, local.depth));

  for (let d = 1; d <= maxDepth && frontier.length; d++) {
    const next: string[] = [];
    for (const id of frontier) {
      const a = g.adjacency.get(id);
      if (!a) continue;
      if (local.outgoing) {
        for (const t of a.out) {
          const l = g.links.find((x) => x.source === id && x.target === t);
          if (l) treeEdges.push(l);
          if (!visited.has(t)) {
            visited.set(t, d);
            next.push(t);
          }
        }
      }
      if (local.incoming) {
        for (const s of a.in) {
          const l = g.links.find((x) => x.source === s && x.target === id);
          if (l) treeEdges.push(l);
          if (!visited.has(s)) {
            visited.set(s, d);
            next.push(s);
          }
        }
      }
    }
    frontier = next;
  }

  const inSet = (id: string) => visited.has(id);
  const links = local.neighborLinks
    ? g.links.filter((l) => inSet(l.source) && inSet(l.target))
    : [...new Map(treeEdges.map((l) => [`${l.source}|${l.target}|${l.kind}`, l])).values()];
  const nodes = [...visited.keys()]
    .map((id) => g.byId.get(id))
    .filter((n): n is GraphNode => !!n)
    .map((n) => ({ ...n, depth: visited.get(n.id) ?? 0, degree: 0 }));

  return indexGraph(nodes, links, 0);
}
